<script setup lang="ts">
import { computed } from 'vue'

import { FlexComponent, PlatformIconComponent, TypographyComponent } from '..'
import { platformProfileUrl } from '../../utils'
import type { TypographyVariants } from '../types/ui'

const props = withDefaults(
  defineProps<{
    handle: string
    /** Leave it out to show the handle without an icon, e.g. for an unrecognised platform. */
    platform?: string
    /**
     * Links the handle to the account's profile on `platform`. Turn it off where a parent owns the
     * click, such as a content card. A platform with no profile URL always shows plain text.
     */
    isLinked?: boolean
    variant?: Extract<TypographyVariants, 'body-small' | 'body-medium'>
    iconSize?: number
  }>(),
  { platform: undefined, isLinked: true, variant: 'body-medium', iconSize: 18 },
)

const href = computed(() => (props.isLinked ? platformProfileUrl(props.platform, props.handle) : undefined))
</script>
<template>
  <FlexComponent :gap="4" align="center" class="platform-handle">
    <PlatformIconComponent v-if="platform" :platform="platform" :size="iconSize" class="platform-handle__icon" />
    <a v-if="href" :href="href" target="_blank" rel="noopener noreferrer" class="platform-handle__link" @click.stop>
      <TypographyComponent
        :variant="variant"
        text-decoration="underline"
        cursor="pointer"
        text-truncate
        :title="handle"
        class="platform-handle__label"
      >
        {{ handle }}
      </TypographyComponent>
    </a>
    <TypographyComponent v-else :variant="variant" text-truncate :title="handle" class="platform-handle__label platform-handle__text">
      {{ handle }}
    </TypographyComponent>
  </FlexComponent>
</template>
<style scoped lang="scss">
.platform-handle {
  min-width: 0;
  max-width: 100%;
}

.platform-handle__icon {
  flex-shrink: 0;
}

.platform-handle__link {
  display: block;
  min-width: 0;
  color: inherit;
}

/* `body-small` renders a <span>, which would take its line box from the link around it and sit low. */
.platform-handle__label {
  display: block;
  min-width: 0;
}
</style>
